import { ContentPageHeader } from '@/components/global';
import { DeleteStockTakeButton } from '@/components/inventory';
import { ProductIdentity } from '@/components/products';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { apiFetch } from '@/lib/api';
import { formatCaseUnits } from '@/lib/formatCaseUnits';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface StockTakeLine {
  id: string;
  productId: string;
  systemUnits: number;
  casesCounted: number;
  leftoverUnits: number;
  varianceUnits: number;
  varianceValueGbp: number;
  isDeferred?: boolean;
  batchNumber?: string | null;
  expiryDate?: string | null;
  product?: {
    id: string;
    name: string;
    sku?: string;
    unitsPerCase?: number;
    imageUrl?: string | null;
  };
}

interface StockTakeDetail {
  id: string;
  status: string;
  isInitial?: boolean;
  totalVarianceGbp: number;
  linesWithVariance: number;
  warehouse?: { name: string };
  lines?: StockTakeLine[];
}

function countedUnits(line: StockTakeLine, unitsPerCase: number): number {
  return line.casesCounted * unitsPerCase + line.leftoverUnits;
}

function hasPhysicalCount(line: StockTakeLine): boolean {
  return line.casesCounted > 0 || line.leftoverUnits > 0;
}

export default function StockTakeReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [st, setSt] = useState<StockTakeDetail | null>(null);
  const [approving, setApproving] = useState(false);
  const [returning, setReturning] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const res = await apiFetch(`/api/v1/stock-takes/${id}`);
    if (res.ok) setSt(await res.json());
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function returnToCounting() {
    if (!id) return;
    setReturning(true);
    const res = await apiFetch(`/api/v1/stock-takes/${id}/return-to-counting`, { method: 'PATCH' });
    setReturning(false);
    if (res.ok) {
      window.location.href = `/inventory/stock-takes/${id}/count`;
    }
  }

  async function approve() {
    if (!id) return;
    setApproving(true);
    await apiFetch(`/api/v1/stock-takes/${id}/approve`, { method: 'PATCH' });
    await load();
    setApproving(false);
  }

  const lines = st?.lines ?? [];
  const isInitial = st?.isInitial === true;
  const countedLines = lines.filter((line) => !line.isDeferred && hasPhysicalCount(line)).length;
  const deferredLines = lines.filter((line) => line.isDeferred).length;
  const varianceLines = lines.filter((line) => line.varianceUnits !== 0);

  return (
    <div className="space-y-6">
      <ContentPageHeader
        title={`Review — ${st?.warehouse?.name ?? 'Stock take'}`}
        back={{ to: '/inventory/stock-takes', label: 'Stock takes' }}
        badge={(
          <div className="flex items-center gap-2">
            {st?.isInitial && <Badge variant="outline">Opening stock</Badge>}
            {st?.status && <Badge>{st.status}</Badge>}
          </div>
        )}
        actions={id && st ? (
          <DeleteStockTakeButton
            stockTakeId={id}
            status={st.status}
            onDeleted={() => navigate('/inventory/stock-takes')}
          />
        ) : null}
      />

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-base">
            {isInitial ? 'Opening stock' : 'Variance summary'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {isInitial ? (
            <>
              <p className="text-lg font-medium">
                Opening value: £{Number(st?.totalVarianceGbp ?? 0).toFixed(2)}
              </p>
              <p className="text-sm text-muted-foreground">
                Counted: {countedLines} of {lines.length} SKUs
                {deferredLines > 0 ? ` · ${deferredLines} deferred` : ''}
              </p>
              <p className="text-sm text-muted-foreground">
                This is an opening count from zero, not a recount against previous system stock.
                Deferred products are skipped and do not book stock.
              </p>
            </>
          ) : (
            <>
              <p className="text-lg font-medium">
                Total variance: £{Number(st?.totalVarianceGbp ?? 0).toFixed(2)}
              </p>
              <p className="text-sm text-muted-foreground">
                Lines with variance: {st?.linesWithVariance ?? varianceLines.length} of {lines.length}
              </p>
              <p className="text-sm text-muted-foreground">
                Counted: {countedLines} of {lines.length} SKUs
                {deferredLines > 0 ? ` · ${deferredLines} deferred` : ''}
              </p>
              {varianceLines.length === 0 && lines.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  All counted lines match system stock. Approve to close this stock take.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                {!isInitial && <TableHead className="hidden sm:table-cell">System</TableHead>}
                <TableHead>Counted</TableHead>
                {!isInitial && <TableHead className="text-right">Variance</TableHead>}
                <TableHead className="hidden text-right md:table-cell">
                  {isInitial ? 'Opening £' : 'Value £'}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isInitial ? 3 : 5} className="py-8 text-center text-muted-foreground">
                    No products in this stock take.
                  </TableCell>
                </TableRow>
              ) : (
                lines.map((line) => {
                  const upc = line.product?.unitsPerCase ?? 1;
                  const counted = countedUnits(line, upc);
                  const wasCounted = !line.isDeferred && hasPhysicalCount(line);
                  const hasVariance = !isInitial && !line.isDeferred && line.varianceUnits !== 0;

                  return (
                    <TableRow
                      key={line.id}
                      className={cn(hasVariance && 'bg-amber-50/60 dark:bg-amber-950/20')}
                    >
                      <TableCell>
                        <ProductIdentity
                          name={line.product?.name ?? '—'}
                          sku={line.product?.sku}
                          product={line.product}
                          productId={line.productId}
                          size="sm"
                        />
                      </TableCell>
                      {!isInitial && (
                        <TableCell className="hidden sm:table-cell">{formatCaseUnits(line.systemUnits, upc)}</TableCell>
                      )}
                      <TableCell>
                        {wasCounted ? (
                          <>
                            {formatCaseUnits(counted, upc)}
                            {line.batchNumber && (
                              <div className="text-xs text-muted-foreground">
                                Batch {line.batchNumber}
                                {line.expiryDate ? ` · exp ${line.expiryDate}` : ''}
                              </div>
                            )}
                          </>
                        ) : line.isDeferred ? (
                          <span className="text-muted-foreground">Deferred</span>
                        ) : (
                          <span className="text-muted-foreground">Not counted</span>
                        )}
                      </TableCell>
                      {!isInitial && (
                        <TableCell className={cn(
                          'text-right tabular-nums',
                          hasVariance && 'font-medium text-amber-800 dark:text-amber-200'
                        )}>
                          {wasCounted ? `${line.varianceUnits} u` : '—'}
                        </TableCell>
                      )}
                      <TableCell className={cn(
                        'hidden text-right tabular-nums md:table-cell',
                        hasVariance && 'font-medium text-amber-800 dark:text-amber-200'
                      )}>
                        {wasCounted ? `£${Number(line.varianceValueGbp).toFixed(2)}` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        {st?.status === 'REVIEW' && (
          <>
            <Button onClick={approve} disabled={approving}>
              {approving ? 'Approving…' : isInitial ? 'Approve opening stock' : 'Approve adjustments'}
            </Button>
            <Button variant="outline" onClick={returnToCounting} disabled={returning}>
              {returning ? 'Returning…' : 'Return to counting'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
